import { redirect, type LoaderFunctionArgs } from "react-router";
import { supabase } from "@shared/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

export const RESULTS_LOADER_ID = "resultsLoader";

type SpecificationRow =
  Database["public"]["Tables"]["specifications"]["Row"];
type SpecificationDatasetRow =
  Database["public"]["Tables"]["specification_datasets"]["Row"];
type SpecificationRunRow =
  Database["public"]["Tables"]["specification_runs"]["Row"];
type SpecificationRunLinkRow =
  Database["public"]["Tables"]["specification_run_test_runs"]["Row"];
type TestRunRow = Database["public"]["Tables"]["test_runs"]["Row"];
type TestResultRow = Database["public"]["Tables"]["test_results"]["Row"];
type TestDataSetRow = Database["public"]["Tables"]["test_data_sets"]["Row"];
type TestDataEntryRow =
  Database["public"]["Tables"]["test_data_entries"]["Row"];
type RobustnessScoreRow =
  Database["public"]["Tables"]["robustness_score"]["Row"];

type SpecificationDatasetWithDataset = SpecificationDatasetRow & {
  test_data_sets?: Pick<
    TestDataSetRow,
    "id" | "name" | "dataset_kind" | "creation_method" | "type" | "parent_id"
  > | null;
};

type SpecificationWithDatasets = SpecificationRow & {
  specification_datasets?: SpecificationDatasetWithDataset[] | null;
};

export type SpecificationRunSummary = Pick<
  SpecificationRunRow,
  "id" | "specification_id" | "status" | "started_at" | "completed_at"
>;

export type SpecificationWithLatestRun = SpecificationWithDatasets & {
  latest_run: SpecificationRunSummary | null;
};

export type RobustnessScoreSummary = {
  id: string;
  derivative_dataset_id: string;
  derivative_dataset_name: string;
  derivative_creation_method: string | null;
  robust_accuracy: number;
  metrics: Record<string, unknown> | null;
};

type PrimaryResultRow = TestResultRow & {
  test_data_entries:
    | Pick<TestDataEntryRow, "id" | "input" | "expected_output">
    | null;
};

type DerivativeResultRow = TestResultRow & {
  test_run_id: string;
  test_data_entries:
    | Pick<
        TestDataEntryRow,
        "id" | "input" | "expected_output" | "parent_id" | "test_data_set_id"
      >
    | null;
};

export type SpecRunTestResult = {
  id: string;
  test_data_entry_id: string;
  input: string | null;
  expected_output: string | null;
  output: string;
  correctness: number | null;
  derivative_results: Array<{
    id: string;
    test_data_entry_id: string;
    output: string;
    correctness: number | null;
    input: string | null;
    expected_output: string | null;
    dataset_id: string;
    dataset_name: string;
    creation_method: string | null;
  }>;
  robustness_count: number;
  robustness_passed: number;
};

export type SpecRunDetail = {
  spec_run: SpecificationRunSummary;
  specification: SpecificationWithLatestRun | null;
  primary_run: TestRunRow | null;
  primary_dataset: TestDataSetRow | null;
  derivative_runs: Array<{
    run: TestRunRow;
    dataset: TestDataSetRow | null;
  }>;
  robustness_scores: RobustnessScoreSummary[];
  test_results: SpecRunTestResult[];
  stats: { correct: number; partial: number; incorrect: number };
};

export interface ResultsLoaderData {
  projectId: string;
  projectTitle: string;
  specifications: SpecificationWithLatestRun[];
  selectedSpecRunId: string | null;
  selectedSpecRun: SpecRunDetail | null;
}

const percent = (value: number, total: number) =>
  total === 0 ? 0 : Math.round((value / total) * 100);

export const resultsLoader = async ({
  params,
  request,
}: LoaderFunctionArgs): Promise<ResultsLoaderData> => {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    throw redirect("/auth");
  }

  const projectId = params["projectId"] || params["evalId"];
  if (!projectId) {
    throw new Response("Project ID required", { status: 400 });
  }

  const url = new URL(request.url);
  const selectedSpecRunId = url.searchParams.get("specRunId");

  const { data: project, error: projectError } = await supabase
    .from("projects")
    .select("id, title")
    .eq("id", projectId)
    .single();

  if (projectError) {
    throw projectError;
  }
  if (!project) {
    throw new Response("Project not found", { status: 404 });
  }

  const { data: specifications, error: specificationsError } = await supabase
    .from("specifications")
    .select(
      `*, specification_datasets(*, test_data_sets!specification_datasets_dataset_id_fkey(id, name, dataset_kind, creation_method, type, parent_id))`,
    )
    .eq("project_id", projectId)
    .order("created_at", { ascending: false });

  if (specificationsError) {
    throw specificationsError;
  }

  const { data: specRuns, error: specRunsError } = await supabase
    .from("specification_runs")
    .select("id, specification_id, status, started_at, completed_at")
    .eq("project_id", projectId)
    .order("started_at", { ascending: false });

  if (specRunsError) {
    throw specRunsError;
  }

  const latestRunBySpec = new Map<string, SpecificationRunSummary>();
  (specRuns ?? []).forEach((run) => {
    if (!latestRunBySpec.has(run.specification_id)) {
      latestRunBySpec.set(run.specification_id, run);
    }
  });

  const specificationsWithRuns = (specifications ?? [])
    .map((spec) => ({
      ...(spec as SpecificationWithDatasets),
      latest_run: latestRunBySpec.get(spec.id) ?? null,
    }))
    .filter((spec) => spec.latest_run !== null) as SpecificationWithLatestRun[];

  let selectedSpecRun: SpecRunDetail | null = null;

  if (selectedSpecRunId) {
    const { data: specRun, error: specRunError } = await supabase
      .from("specification_runs")
      .select("id, specification_id, status, started_at, completed_at")
      .eq("id", selectedSpecRunId)
      .eq("project_id", projectId)
      .maybeSingle();

    if (specRunError) {
      throw specRunError;
    }

    if (specRun) {
      const { data: runLinks, error: runLinksError } = await supabase
        .from("specification_run_test_runs")
        .select("test_run_id, role")
        .eq("specification_run_id", specRun.id)
        .returns<Pick<SpecificationRunLinkRow, "test_run_id" | "role">[]>();

      if (runLinksError) {
        throw runLinksError;
      }

      const testRunIds = (runLinks ?? []).map((link) => link.test_run_id);
      const { data: testRuns, error: testRunsError } = testRunIds.length
        ? await supabase
            .from("test_runs")
            .select("*")
            .in("id", testRunIds)
            .returns<TestRunRow[]>()
        : { data: [], error: null };

      if (testRunsError) {
        throw testRunsError;
      }

      const testRunMap = new Map(
        (testRuns ?? []).map((run) => [run.id, run]),
      );

      const primaryLink = (runLinks ?? []).find((link) => link.role === "primary");
      const primaryRun = primaryLink
        ? testRunMap.get(primaryLink.test_run_id) ?? null
        : null;

      const derivativeLinks = (runLinks ?? []).filter(
        (link) => link.role === "adversarial",
      );

      const datasetIds = new Set<string>();
      if (primaryRun?.test_data_set_id) {
        datasetIds.add(primaryRun.test_data_set_id);
      }
      derivativeLinks.forEach((link) => {
        const run = testRunMap.get(link.test_run_id);
        if (run?.test_data_set_id) {
          datasetIds.add(run.test_data_set_id);
        }
      });

      const { data: datasets, error: datasetsError } =
        datasetIds.size > 0
          ? await supabase
              .from("test_data_sets")
              .select("id, name, dataset_kind, creation_method, parent_id, type")
              .in("id", Array.from(datasetIds))
              .returns<TestDataSetRow[]>()
          : { data: [], error: null };

      if (datasetsError) {
        throw datasetsError;
      }

      const datasetMap = new Map(
        (datasets ?? []).map((dataset) => [dataset.id, dataset]),
      );

      const { data: robustnessRows, error: robustnessError } = await supabase
        .from("robustness_score")
        .select("*")
        .eq("specification_run_id", specRun.id)
        .returns<RobustnessScoreRow[]>();

      if (robustnessError) {
        throw robustnessError;
      }

      const robustnessScores = (robustnessRows ?? []).map((row) => {
        const dataset = datasetMap.get(row.derivative_data_set);
        return {
          id: row.id,
          derivative_dataset_id: row.derivative_data_set,
          derivative_dataset_name: dataset?.name ?? "Unknown",
          derivative_creation_method: dataset?.creation_method ?? null,
          robust_accuracy: row.robust_accuracy,
          metrics: (row.metrics as Record<string, unknown> | null) ?? null,
        } satisfies RobustnessScoreSummary;
      });

      const derivativeRuns = derivativeLinks.map((link) => ({
        run: testRunMap.get(link.test_run_id) as TestRunRow,
        dataset: testRunMap.get(link.test_run_id)?.test_data_set_id
          ? datasetMap.get(testRunMap.get(link.test_run_id)!.test_data_set_id!) ?? null
          : null,
      }));

      let testResults: SpecRunTestResult[] = [];
      let stats = { correct: 0, partial: 0, incorrect: 0 };

      if (primaryRun?.id) {
        const { data: primaryResults, error: primaryResultsError } =
          await supabase
            .from("test_results")
            .select(
              `id, test_data_entry_id, output, correctness, created_at, test_data_entries(id, input, expected_output)`
            )
            .eq("test_run_id", primaryRun.id)
            .order("created_at", { ascending: true })
            .returns<PrimaryResultRow[]>();

        if (primaryResultsError) {
          throw primaryResultsError;
        }

        const primaryResultsNormalized = (primaryResults ?? []).map((result) => ({
          id: result.id,
          test_data_entry_id: result.test_data_entry_id,
          input: result.test_data_entries?.input ?? null,
          expected_output: result.test_data_entries?.expected_output ?? null,
          output: result.output,
          correctness: result.correctness,
          derivative_results: [],
          robustness_count: 0,
          robustness_passed: 0,
        })) as SpecRunTestResult[];

        const primaryResultMap = new Map(
          primaryResultsNormalized.map((result) => [result.test_data_entry_id, result]),
        );

        const derivativeRunIds = derivativeLinks
          .map((link) => link.test_run_id)
          .filter(Boolean);

        if (derivativeRunIds.length > 0) {
          const { data: derivativeResults, error: derivativeResultsError } =
            await supabase
              .from("test_results")
              .select(
                `id, test_run_id, test_data_entry_id, output, correctness, created_at, test_data_entries(id, input, expected_output, parent_id, test_data_set_id)`
              )
              .in("test_run_id", derivativeRunIds)
              .order("created_at", { ascending: true })
              .returns<DerivativeResultRow[]>();

          if (derivativeResultsError) {
            throw derivativeResultsError;
          }

          (derivativeResults ?? []).forEach((result) => {
            const entry = result.test_data_entries;
            const parentId = entry?.parent_id ?? null;
            if (!parentId) {
              return;
            }
            const parent = primaryResultMap.get(parentId);
            if (!parent) {
              return;
            }
            const datasetId = entry?.test_data_set_id ?? "";
            const dataset = datasetMap.get(datasetId);
            parent.derivative_results.push({
              id: result.id,
              test_data_entry_id: result.test_data_entry_id,
              output: result.output,
              correctness: result.correctness,
              input: entry?.input ?? null,
              expected_output: entry?.expected_output ?? null,
              dataset_id: datasetId,
              dataset_name: dataset?.name ?? "Unknown",
              creation_method: dataset?.creation_method ?? null,
            });
          });

          primaryResultsNormalized.forEach((result) => {
            if (result.derivative_results.length === 0) {
              return;
            }
            result.robustness_count = result.derivative_results.length;
            result.robustness_passed = result.derivative_results.filter(
              (derivative) => derivative.correctness === 100,
            ).length;
          });
        }

        const totalResults = primaryResultsNormalized.length;
        const correct = primaryResultsNormalized.filter(
          (result) => result.correctness === 100,
        ).length;
        const partial = primaryResultsNormalized.filter(
          (result) =>
            result.correctness !== null &&
            result.correctness > 0 &&
            result.correctness < 100,
        ).length;
        const incorrect = primaryResultsNormalized.filter(
          (result) => result.correctness === 0,
        ).length;

        stats = {
          correct: percent(correct, totalResults),
          partial: percent(partial, totalResults),
          incorrect: percent(incorrect, totalResults),
        };
        testResults = primaryResultsNormalized;
      }

      selectedSpecRun = {
        spec_run: specRun,
        specification:
          specificationsWithRuns.find(
            (spec) => spec.id === specRun.specification_id,
          ) ?? null,
        primary_run: primaryRun,
        primary_dataset: primaryRun?.test_data_set_id
          ? datasetMap.get(primaryRun.test_data_set_id) ?? null
          : null,
        derivative_runs: derivativeRuns,
        robustness_scores: robustnessScores,
        test_results: testResults,
        stats,
      };
    }
  }

  return {
    projectId: project.id,
    projectTitle: project.title,
    specifications: specificationsWithRuns,
    selectedSpecRunId,
    selectedSpecRun,
  };
};
